import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getCourseStudentMe,
  loginCourseStudent,
  logoutCourseStudent,
  setCourseStudentToken,
  clearCourseStudentToken,
} from '../api/courseAuthApi';
import { getCourseStudentToken } from '../utils/courseTokenStorage';
import { getApiError } from '../utils/getApiError';

const CourseAuthContext = createContext(null);

export function CourseAuthProvider({ children }) {
  const [student, setStudent] = useState(null);
  const [token, setToken] = useState(() => getCourseStudentToken());
  const [loading, setLoading] = useState(true);

  const refreshStudent = useCallback(async () => {
    const stored = getCourseStudentToken();
    if (!stored) {
      setStudent(null);
      setToken(null);
      setLoading(false);
      return null;
    }

    try {
      const res = await getCourseStudentMe();
      const me = res?.data?.student || res?.data?.user || res?.data || res;
      setStudent(me);
      setToken(stored);
      return me;
    } catch (err) {
      clearCourseStudentToken();
      setStudent(null);
      setToken(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = getCourseStudentToken();
      if (!stored) {
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }
      try {
        await refreshStudent();
      } catch {
        // invalid token cleared inside refreshStudent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshStudent]);

  const login = useCallback(async (credentials) => {
    const res = await loginCourseStudent(credentials);
    const nextToken = res?.data?.token || res?.token;
    if (!nextToken) {
      throw new Error(getApiError(null, 'Login succeeded but no token was returned.'));
    }
    setCourseStudentToken(nextToken);
    setToken(nextToken);
    const me = await refreshStudent();
    return { token: nextToken, student: me, raw: res };
  }, [refreshStudent]);

  const logout = useCallback(async () => {
    try {
      await logoutCourseStudent();
    } catch {
      clearCourseStudentToken();
    }
    setStudent(null);
    setToken(null);
  }, []);

  const saveSession = useCallback((nextToken, nextStudent) => {
    if (nextToken) {
      setCourseStudentToken(nextToken);
      setToken(nextToken);
    }
    if (nextStudent) {
      setStudent(nextStudent);
    }
  }, []);

  const value = useMemo(
    () => ({
      student,
      token,
      isAuthenticated: Boolean(token),
      loading,
      login,
      logout,
      refreshStudent,
      saveSession,
      setStudent,
    }),
    [student, token, loading, login, logout, refreshStudent, saveSession]
  );

  return <CourseAuthContext.Provider value={value}>{children}</CourseAuthContext.Provider>;
}

export function useCourseAuth() {
  const ctx = useContext(CourseAuthContext);
  if (!ctx) {
    throw new Error('useCourseAuth must be used within CourseAuthProvider');
  }
  return ctx;
}

export default CourseAuthContext;
